import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RequestField } from "./types/RequestsTypes";
import { Room } from "./types/RoomsTypes";
import { RootState, store } from "./store";
import { toast } from "react-toastify";
import { setPaymentLink, setSignStatus } from "./slices/ProposalSlice";
import { setRooms, setLoading, setError } from "./slices/RoomsSlice";

declare global {
  var translate: any;
}

export const widget = createApi({
  reducerPath: "widget",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL,
    prepareHeaders(headers, { getState }) {
      const params = (getState() as RootState).generalSettings.headers;

      if (params?.restaurantName && params.widgetName) {
        headers.set("Restaurant", params.restaurantName);
        if (!params.widgetName) {
          headers.set("Widget", params.widgetName);
        }
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    getGeneralSettings: builder.query({
      query: () => {
        return {
          url: "general/settings",
        };
      },
    }),
    getRequestsSettings: builder.query<{ request_fields: RequestField[] }, {}>({
      query: () => {
        return {
          url: "em/settings",
        };
      },
    }),
    getTranslation: builder.query<any, string>({
      query: (locale) => {
        return {
          url: `widget/translate/${locale}`,
          headers: {
            Widget: "em",
          },
        };
      },

      transformResponse: (response) => {
        globalThis.translate = response;
      },
    }),
    createRequest: builder.mutation({
      query: (data) => ({
        url: "em/request",
        method: "POST",
        body: data,
      }),
      transformResponse() {
        return toast.success("Request create successfully!");
      },
      transformErrorResponse() {
        return toast.error("Something went wrong");
      },
    }),
    fetchRooms: builder.query<Room[], void>({
      query: () => {
        return {
          url: "em/rooms",
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setLoading(true));
          const { data } = await queryFulfilled;
          dispatch(setRooms(data));
        } catch (error) {
          dispatch(setError("Failed to fetch rooms"));
        }
      },
    }),
    fetchRoomDetails: builder.query<Room, number>({
      query: (id_room) => {
        return {
          url: `em/rooms/${id_room}`,
        };
      },
    }),
    fetchProposalData: builder.query({
      query: (id_proposal) => {
        return {
          url: `em/proposal/event/${id_proposal}`,
        };
      },
    }),
    createSignature: builder.mutation({
      query: (data) => ({
        url: "em/proposal/signature",
        method: "POST",
        body: data,
      }),
      transformResponse(response: {
        sign_status: string;
        payment_link?: string;
      }) {
        store.dispatch(setSignStatus(response?.sign_status));
        if (
          Array.isArray(response?.payment_link) &&
          typeof response.payment_link[0] === "string"
        ) {
          store.dispatch(setPaymentLink(response.payment_link));
        }

        return toast.success("Signert!");
      },
      transformErrorResponse() {
        return toast.error("Something went wrong");
      },
    }),
    fetchRegularProposalData: builder.query({
      query: (id_proposal) => {
        return {
          url: `em/proposal/booking/${id_proposal}`,
        };
      },
    }),
    createRegularSignature: builder.mutation({
      query: (data) => ({
        url: "em/proposal/signature",
        method: "POST",
        body: data,
      }),
      transformResponse(response: {
        sign_status: string;
        payment_link?: string[] | boolean;
      }) {
        store.dispatch(setSignStatus(response?.sign_status));
        if (
          Array.isArray(response?.payment_link) &&
          typeof response.payment_link[0] === "string"
        ) {
          store.dispatch(setPaymentLink(response.payment_link));
        }

        return toast.success("Signert!");
      },
      transformErrorResponse() {
        return toast.error("Something went wrong");
      },
    }),
    fetch_availability: builder.query({
      query: ({ id_room, date }) => ({
        url: `em/booking/room/${id_room}/availability/month/${date}`,
      }),
    }),
    createBooking: builder.mutation({
      query: (data) => ({
        url: "em/booking",
        method: "POST",
        body: data,
      }),
      transformResponse() {
        return toast.success("Booking created successfully!");
      },
      transformErrorResponse() {
        return toast.error("Something went wrong");
      },
    }),
    sendEmail: builder.mutation({
      query: (data) => ({
        url: "em/proposal/contact",
        method: "POST",
        body: data,
      }),
      transformResponse() {
        return toast.success("Email sent successfully!");
      },
      transformErrorResponse() {
        return toast.error("Something went wrong");
      },
    }),
  }),
  refetchOnMountOrArgChange: true,
});

export const api = widget.endpoints;
export const {
  useGetRequestsSettingsQuery,
  useGetTranslationQuery,
  useCreateRequestMutation,
  useCreateSignatureMutation,
  useFetch_availabilityQuery,
  useCreateBookingMutation,
  useSendEmailMutation,
  useCreateRegularSignatureMutation,
  useFetchRoomsQuery,
} = widget;
